import React, { useState, useEffect } from 'react';
import './AvailabilityModal.css';

const API_URL = 'https://ryyy109uc3.execute-api.us-east-1.amazonaws.com/prod';

const AvailabilityModal = ({ isOpen, onClose, onSave }) => {
  const [date, setDate] = useState('');
  const [isAvailable, setIsAvailable] = useState(true);
  const [reason, setReason] = useState('');
  const [userId, setUserId] = useState('band-member'); // In a real app, this would come from authentication
  const [availabilityRecords, setAvailabilityRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch availability records when the modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAvailabilityRecords();
    }
  }, [isOpen]);

  const fetchAvailabilityRecords = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch events which includes availability data
      const response = await fetch(`${API_URL}/events`);
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Filter for unavailability events
      const unavailabilityEvents = Array.isArray(data) 
        ? data.filter(event => event.eventType === 'unavailable')
        : [];
      
      setAvailabilityRecords(unavailabilityEvents);
    } catch (error) {
      console.error('Error fetching availability records:', error);
      setError('Failed to load availability records');
    }
    setIsLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!date) {
      alert('Please select a date');
      return;
    }
    
    // Create availability object
    const availabilityData = {
      date,
      isAvailable,
      userId,
      reason: isAvailable ? '' : reason,
      updatedAt: new Date().toISOString()
    };
    
    onSave(availabilityData);
  };

  const handleDeleteAvailability = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this availability record?')) {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    try {
      console.log('Deleting availability with ID:', eventId);
      
      const response = await fetch(`${API_URL}/availability`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ id: eventId })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log("Availability deleted successfully:", result);
      
      // Refresh the availability records
      fetchAvailabilityRecords();
    } catch (error) {
      console.error('Error deleting availability:', error);
      setError(`Failed to delete availability: ${error.message}`);
    }
    setIsLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Set Availability</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="date">Date:</label>
            <input 
              type="date" 
              id="date" 
              value={date} 
              onChange={(e) => setDate(e.target.value)} 
              required 
            />
          </div>
          
          <div className="form-group">
            <label>Availability:</label>
            <div className="radio-group">
              <label>
                <input 
                  type="radio" 
                  name="availability" 
                  checked={isAvailable} 
                  onChange={() => setIsAvailable(true)} 
                />
                Available
              </label>
              <label>
                <input 
                  type="radio" 
                  name="availability" 
                  checked={!isAvailable} 
                  onChange={() => setIsAvailable(false)} 
                />
                Unavailable
              </label>
            </div>
          </div>
          
          {!isAvailable && (
            <div className="form-group">
              <label htmlFor="reason">Reason (optional):</label>
              <textarea 
                id="reason" 
                value={reason} 
                onChange={(e) => setReason(e.target.value)} 
                rows="3"
              />
            </div>
          )}
          
          <div className="modal-footer">
            <button type="submit" className="save-button">Save</button>
          </div>
        </form>
        
        {/* Existing Unavailability Records */}
        <div className="availability-records">
          <h3>Your Unavailability Records</h3>
          
          {isLoading ? (
            <p>Loading records...</p>
          ) : error ? (
            <p className="error">{error}</p>
          ) : availabilityRecords.length === 0 ? (
            <p>No unavailability records found.</p>
          ) : (
            <ul className="records-list">
              {availabilityRecords.map(record => {
                // Extract date from the startTime
                const date = record.startTime ? record.startTime.split('T')[0] : 'Unknown date';
                
                return (
                  <li key={record.eventId} className="record-item">
                    <div className="record-info">
                      <span className="record-date">{date}</span>
                      <span className="record-reason">{record.description || 'No reason provided'}</span>
                    </div>
                    <button 
                      className="delete-button small" 
                      onClick={() => handleDeleteAvailability(record.eventId)}
                    >
                      Delete
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AvailabilityModal;
